import React, { useEffect, useState } from "react";
import styles from "./VendorDetailPage.module.css";
import WebService from "../Services/WebService";
import { useParams } from "react-router-dom";
import {
  FaMapMarkerAlt, FaPhone, FaBoxOpen, FaTag
} from "react-icons/fa";
import LedgerStatement from "../Accounts/LedgerStatement";
import RecordVendorPaymentModal from "./RecordVendorPaymentModal";
import { LedgerStatement as LedgerStatementData } from "../Models/Models";

interface RawMaterial {
  id: number;
  name: string;
}

interface Address {
  complete_address?: string;
  address?: string;
  landmark?: string;
}

interface Vendor {
  user_id: number;
  vendor_id: number;
  username: string;
  email: string;
  mobile_number: string;
  user_status: string;
  company_name: string;
  gst_number: string;
  address: Address[];
  raw_materials: RawMaterial[];
}

const VendorDetailPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0);
  const [outstanding, setOutstanding] = useState<number | null>(null);

useEffect(() => {
  if (!vendorId) return;
  setLoading(true);
  WebService.getAPI<any>({ action: `/v1/door-management/vendors?vendor_id=${vendorId}` })
    .then((res) => {
      if (
        res.status_code === 200 &&
        Array.isArray(res.data?.vendors) &&
        res.data.vendors.length > 0
      ) {
        const v = res.data.vendors[0];
        const mapped: Vendor = {
          ...v,
          raw_materials: (v.raw_materials || []).map((cat: any) => ({
            id: cat.category_id,
            name: cat.category_name,
          })),
        };
        setVendor(mapped);
      } else {
        setVendor(null);
      }
    })
    .finally(() => setLoading(false));
}, [vendorId]);

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (!vendor) return <div className={styles.notFound}>Vendor not found.</div>;

  return (
    <div className={styles.vendorDetailWrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.vendorIcon}><FaBoxOpen size={36} /></div>
          <div>
            <div className={styles.vendorName}>{vendor.company_name || "—"}</div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.statusActive}>Active</span>
          <span className={styles.statusVerified}>Verified</span>
       </div>
      </div>

      {/* Details Card */}
      <div className={styles.detailCard}>
        <div className={styles.detailRow}>
          <div>
            <div className={styles.detailLabel}><FaPhone /> Contact Person</div>
            <div className={styles.detailValue}>{vendor.username || "—"}</div>
          </div>
          <div>
            <div className={styles.detailLabel}><FaTag /> GST Number</div>
            <div className={styles.detailValue}>{vendor.gst_number || "—"}</div>
          </div>
        </div>
        <div className={styles.detailRow}>
          <div>
            <div className={styles.detailLabel}><FaPhone /> Mobile Number</div>
            <div className={styles.detailValue}>{vendor.mobile_number || "—"}</div>
          </div>
          <div>
            <div className={styles.detailLabel}><FaMapMarkerAlt /> Address</div>
            <div className={styles.detailValue}>
              {Array.isArray(vendor.address)
                ? vendor.address?.[0]?.complete_address ||
                  vendor.address?.[0]?.address ||
                  "—"
                : (vendor as any).address || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Raw Materials Supplied */}
      <div className={styles.materialSection}>
        <div className={styles.materialHeader}>
          <span className={styles.sectionTitle}>Raw Materials Supplied</span>
          <span className={styles.materialCount}>
            {vendor.raw_materials?.length || 0} Items
          </span>
        </div>
        <div className={styles.materialList}>
{(vendor.raw_materials || []).map((mat, idx) => (
  <div key={mat.id} className={styles.materialCard}>
    <span className={styles.materialNumber}>{idx + 1}.</span>
    <span className={styles.materialName}>{mat.name}</span>
  </div>
))}

        </div>
      </div>

      <div className={styles.ledgerSection}>
        <div className={styles.ledgerHeader}>
          <span className={styles.sectionTitle}>Vendor Ledger</span>
          <button
            type="button"
            className={styles.editBtn}
            onClick={() => setPaymentOpen(true)}
          >
            + Record Payment
          </button>
        </div>
        <LedgerStatement
          partyType="VENDOR"
          partyId={Number(vendorId)}
          refreshKey={ledgerRefreshKey}
          onLoaded={(statement: LedgerStatementData | null) => {
            // Typed as number, but the API has been seen sending null/"" for a settled party.
            const value = statement?.totals?.outstanding as number | string | null | undefined;
            setOutstanding(value == null || value === "" ? null : Number(value));
          }}
        />
      </div>

      {paymentOpen && (
        <RecordVendorPaymentModal
          open={paymentOpen}
          vendorId={Number(vendorId)}
          vendorName={vendor.company_name || vendor.username}
          outstanding={outstanding}
          onClose={() => setPaymentOpen(false)}
          onSuccess={() => {
            setLedgerRefreshKey((k) => k + 1);
            setPaymentOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default VendorDetailPage;
